import { Region } from '../../core/types';
import { Logger } from '../../utils/logger';

export type GraphQLRequest = {
  query: string;
  variables?: Record<string, unknown>;
};

export class CreatelyApiClient {
  private baseUrl: string;
  private timeoutMs: number;
  private retries: number;

  constructor(params: { baseUrl: string; timeoutMs?: number; retries?: number }) {
    this.baseUrl = params.baseUrl.replace(/\/$/, '');
    this.timeoutMs = params.timeoutMs ?? 10_000;
    this.retries = params.retries ?? 2;
  }

  static fromEnv(
    env: {CREATELY_API_BASE_URL?: string; CREATELY_API_TIMEOUT?: string; CREATELY_API_RETRY_ATTEMPTS?: string, [key: string]: any },
    region: Region = Region.US
  ) {
    let baseUrl = env.CREATELY_API_BASE_URL || 'https://api.creately.com/api/v0';
    if (region && region !== Region.US) {
      const regionalBaseUrl = env.CREATELY_API_REGIONAL_BASE_URL;
      baseUrl = regionalBaseUrl.replace('{region}', region);
    }
    const timeoutMs = Number(env.CREATELY_API_TIMEOUT || '10000');
    const retries = Number(env.CREATELY_API_RETRY_ATTEMPTS || '2');
    return new CreatelyApiClient({ baseUrl, timeoutMs, retries });
  }

  async graphql<T>(req: GraphQLRequest, token: string): Promise<T> {
    let attempt = 0;
    let lastErr: unknown;
    while (attempt <= this.retries) {
      attempt++;
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), this.timeoutMs);
        const resp = await fetch(this.baseUrl, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'creately-gravity-token': token,
          },
          body: JSON.stringify(req),
          signal: controller.signal,
        });
        clearTimeout(timer);

        if (resp.status >= 500) {
          throw new Error(`Creately API 5xx: ${resp.status}`);
        }

        const json = (await resp.json()) as any;
        if (json.errors && json.errors.length) {
          const msg = json.errors.map((e: any) => e.message).join('; ');
          throw new Error(`Creately API GraphQL error: ${msg}`);
        }
        return json.data as T;
      } catch (err) {
        lastErr = err;
        const retryable = err instanceof Error && (err.name === 'AbortError' || /5\d\d/.test(err.message) || /network/i.test(err.message));
        Logger.warn('CreatelyAPI', `GraphQL request failed (attempt ${attempt})`, (err as any)?.message || err);
        if (!retryable || attempt > this.retries) break;
      }
    }
    Logger.error('CreatelyAPI', 'GraphQL request failed permanently', lastErr);
    throw lastErr instanceof Error ? lastErr : new Error('Creately API request failed');
  }

  async inviteToTeam(
    token: string,
    email: string,
    planId: string,
    role: string = 'member',
    notifyEmail: boolean = true
  ): Promise<boolean> {
    const skipEmail = notifyEmail ? 'false' : 'true';
    const query = `mutation{
    organizationAddMember(orgMember:[
        {
            email: "${email}",
            role: ${role},
            planId: "${planId}",
            skipEmailNotification: ${skipEmail}
        }
    ]){
        status, 
        errorCode
    }
}`;

    const response = await this.graphql<{
      organizationAddMember: { status: boolean; errorCode?: string };
    }>({ query }, token);

    return response.organizationAddMember?.status ?? false;
  }
}

